import { mapOperatorNameToOperatorType, mapOperatorTypeToOperator, numberDurationOperatorOptions, NUMBER_DURATION_DEFAULT_OPERATOR, NUMBER_DURATION_TWO_VALUE_OPERATORS } from '../utils/numberDurationQuickFilterUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import QuickFilterSelect from './QuickFilterSelect';
import { Button } from 'foundations-components/transitional/components/Button';
import { Box } from 'foundations-components/transitional/components/Box';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import UISelect from 'UIComponents/input/UISelect';
import invariant from 'react-utils/invariant';
import once from 'transmute/once';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getDefaultState = once(() => ({
  isActive: false,
  leftInputEnabled: NUMBER_DURATION_TWO_VALUE_OPERATORS.includes(NUMBER_DURATION_DEFAULT_OPERATOR),
  leftValue: 0,
  mainValue: 0,
  operatorType: NUMBER_DURATION_DEFAULT_OPERATOR
}));
const getStateFromCondition = condition => {
  if (condition) {
    const value = 'value' in condition ? condition.value : undefined;
    const highValue = 'highValue' in condition ? condition.highValue : undefined;
    const operatorName = 'name' in condition ? condition.name : undefined;
    const operatorType = mapOperatorNameToOperatorType(operatorName);
    if (NUMBER_DURATION_TWO_VALUE_OPERATORS.includes(operatorType)) {
      return {
        isActive: true,
        leftInputEnabled: true,
        leftValue: Number(value) * 100,
        mainValue: Number(highValue) * 100,
        operatorType
      };
    }
    return {
      isActive: true,
      leftInputEnabled: false,
      leftValue: getDefaultState().leftValue * 100,
      mainValue: Number(value) * 100,
      operatorType
    };
  }
  return getDefaultState();
};
const PercentageQuickFilter = ({
  buttonProperties,
  condition,
  disableTruncation,
  isFirst,
  property,
  onValueChange
}) => {
  const initialState = getStateFromCondition(condition);
  const [isActive, setIsActive] = useState(initialState.isActive);
  const [isOpen, setIsOpen] = useState(false);
  const [leftInputEnabled, setLeftInputEnabled] = useState(initialState.leftInputEnabled);
  const [leftValue, setLeftValue] = useState(initialState.leftValue);
  const [mainValue, setMainValue] = useState(initialState.mainValue);
  const [operatorType, setOperatorType] = useState(initialState.operatorType);
  const resetToDefaultState = useCallback(() => {
    setLeftInputEnabled(getDefaultState().leftInputEnabled);
    setLeftValue(getDefaultState().leftValue);
    setMainValue(getDefaultState().mainValue);
    setOperatorType(getDefaultState().operatorType);
    setIsActive(getDefaultState().isActive);
  }, []);
  useEffect(() => {
    if (!condition) {
      resetToDefaultState();
    }
  }, [condition, resetToDefaultState]);
  const onApplyChanges = useCallback(() => {
    const Operator = mapOperatorTypeToOperator(operatorType);
    invariant(Operator, `Unable to find a operator for operator type "${operatorType}"`);
    if (leftInputEnabled) {
      const highValue = Math.max(leftValue, mainValue);
      const lowValue = Math.min(leftValue, mainValue);
      setLeftValue(lowValue);
      setMainValue(highValue);
      onValueChange(Operator.of(property).set('value', lowValue / 100).set('highValue', highValue / 100));
    } else {
      onValueChange(Operator.of(property).set('value', mainValue / 100));
    }
    setIsOpen(false);
    setIsActive(true);
  }, [leftInputEnabled, leftValue, mainValue, onValueChange, operatorType, property]);
  const onClearChanges = useCallback(() => {
    if (condition) {
      onValueChange();
      resetToDefaultState();
    }
  }, [condition, onValueChange, resetToDefaultState]);
  const onToggleOpen = () => {
    setIsOpen(currentIsOpen => !currentIsOpen);
  };
  const onOperatorChange = evt => {
    const newOperator = evt.target.value;
    setOperatorType(newOperator);
    if (NUMBER_DURATION_TWO_VALUE_OPERATORS.includes(newOperator)) {
      setLeftInputEnabled(true);
    } else {
      setLeftInputEnabled(false);
      setLeftValue(getDefaultState().leftValue);
    }
  };
  const onLeftValueChange = evt => {
    setLeftValue(evt.target.value);
  };
  const onMainValueChange = evt => {
    setMainValue(evt.target.value);
  };
  const operatorOptions = useMemo(() => numberDurationOperatorOptions.map(({
    langToken,
    symbol,
    value
  }) => ({
    text: `${symbol} ${I18n.text(langToken)}`,
    value
  })), []);
  return /*#__PURE__*/_jsx(QuickFilterSelect, {
    buttonProperties: buttonProperties,
    buttonText: property.displayName,
    condition: condition,
    disableTruncation: disableTruncation,
    isActive: isActive,
    isFirst: isFirst,
    isOpen: isOpen,
    property: property,
    popoverContent: {
      body: /*#__PURE__*/_jsxs(TFlex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(Box, {
          grow: 2,
          children: /*#__PURE__*/_jsx(NumberInput, {
            "data-selenium-test": "PercentageQuickFilter-leftValue",
            disabled: !leftInputEnabled
            // @ts-expect-error UINumberInput hasn't been properly migrated yet
            ,
            onChange: onLeftValueChange,
            value: leftValue
          })
        }), /*#__PURE__*/_jsx(Text, {
          paddingInlineStart: "100",
          children: "%"
        }), /*#__PURE__*/_jsx(Box, {
          grow: 1,
          paddingInlineStart: "300",
          paddingInlineEnd: "300",
          children: /*#__PURE__*/_jsx(UISelect, {
            "data-selenium-test": "PercentageQuickFilter-comparator",
            menuWidth: 'auto',
            valueRenderer: ({
              text
            }) => text[0],
            onChange: onOperatorChange,
            options: operatorOptions,
            value: operatorType
          })
        }), /*#__PURE__*/_jsx(Box, {
          grow: 2,
          children: /*#__PURE__*/_jsx(NumberInput, {
            "data-selenium-test": "PercentageQuickFilter-rightValue"
            // @ts-expect-error UINumberInput hasn't been properly migrated yet
            ,
            onChange: onMainValueChange,
            value: mainValue
          })
        }), /*#__PURE__*/_jsx(Text, {
          paddingInlineStart: "100",
          children: "%"
        })]
      }),
      footer: /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        direction: "row",
        justify: "between",
        wrap: "nowrap",
        children: [/*#__PURE__*/_jsx(Button, {
          "data-selenium-test": "PercentageQuickFilter-apply",
          size: "small",
          use: "tertiary",
          onClick: onApplyChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.number.actions.apply"
          })
        }), /*#__PURE__*/_jsx(Button, {
          "data-selenium-test": "PercentageQuickFilter-clear",
          size: "small",
          use: "tertiary-light",
          onClick: onClearChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.number.actions.clear"
          })
        })]
      })
    },
    onToggleOpen: onToggleOpen,
    onValueChange: onValueChange,
    width: "350px"
  });
};
export default PercentageQuickFilter;